"use client"

import styled from "@emotion/styled"
import { Button, Pagination } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useCallback, useMemo, useState } from "react"
import {
  extractProperty,
  FacilityContent,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  PortItem,
} from "utils"
import { Location3Icon, PriceIcon, UserIcon } from "../icons"
import PortContainer from "../PortContainer"
import PortTitle from "../PortTitle"
import { getCtaLabel, isMeetingRoom, isOffice, ITEMS_PER_PAGE } from "./constants"

const StyledSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 40px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.port.defaultTextColor};

  padding-block: 60px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-block: 120px;
  }
`

const StyledItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  width: 100%;
  grid-template-columns: 100%;
  gap: 16px;
  justify-items: center;

  ${(props) => props.theme.breakpoints.up("sm")} {
    gap: 32px;
    grid-template-columns: repeat(2, 1fr);
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  width: 100%;
  min-height: max-content;

  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.port.grey};
  background: ${(props) => props.theme.colors.white};
  overflow: hidden;
`

const StyledImageWrapper = styled.div`
  width: 100%;
  height: 259px;
  display: flex;
  flex-shrink: 0;
  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`

const StyledItemContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px 24px;
  width: 100%;
  gap: 16px;
  flex: 1;
`

const StyledItemName = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 24px;
  font-weight: 300;
  line-height: 150%;
  text-transform: capitalize;
`

const StyledDataList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  flex: 1;
`

const StyledDataItem = styled.li`
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  span,
  address {
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 150%;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`

const StyledCardButton = styled(Button)`
  width: 100%;
  height: 40px;
  border-radius: 45px;
  font-size: 18px;
  font-weight: 700;
  text-transform: none;
  margin-top: auto;
`

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0 60px;

  font-family: var(--rag) !important;
  .MuiPagination-ul {
    gap: 16px;
  }

  .MuiPaginationItem-page {
    font-family: var(--rag);
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
    border-radius: 50%;
    font-size: 16px;
    font-weight: 400;
    min-width: 24px;
    height: 24px;
    border: 1px solid ${({ theme }) => theme.colors.port.grey};
    margin: 0;
  }

  .MuiPaginationItem-page.Mui-selected {
    background-color: #0000fa;
    color: ${({ theme }) => theme.colors.white};
  }
`

type PortFacilitiesInnerPageProps = {
  data: any
  ports?: PortItem[]
}

const PortFacilitiesInnerPage = ({ data, ports }: PortFacilitiesInnerPageProps) => {
  const t = useTranslations("portFacilitiesInnerPage")
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const subtitle = extractProperty(data, "subtitle")

  const [currentPage, setCurrentPage] = useState(1)

  const allFacilities: FacilityContent[] = useMemo(() => {
    const facilities = ports?.flatMap((port) => port.portFacilities || []) || []
    const itemsList = extractProperty(data, "facilities") || []
    if (itemsList.length > 0) {
      return itemsList
        .map((item: any) => facilities.find((f) => item.url === f.url))
        .filter(Boolean) as FacilityContent[]
    }
    return facilities
  }, [data, ports])

  const totalPages = Math.ceil(allFacilities.length / ITEMS_PER_PAGE)
  const paginatedFacilities = allFacilities.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  )

  const handlePageChange = useCallback((_: any, page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  if (!blackTitle && !blueTitle && allFacilities.length === 0) {
    return null
  }

  return (
    <StyledSection>
      <PortContainer paddingInline={20}>
        <PortTitle
          normalText={blackTitle}
          highlightedText={blueTitle}
          centered
          subtitle={subtitle || ""}
          subtitleSx={{ fontWeight: { xs: "300" } }}
        />
      </PortContainer>

      <PortContainer paddingInline={20} maxWidthXL={1440}>
        <StyledItemList>
          {paginatedFacilities.map(
            (
              { facilityName, facilityType, image, address, amountOfPeople, price, url: faciUrl },
              index,
            ) => (
              <StyledItem key={index}>
                <StyledImageWrapper>
                  {image && (
                    <Image
                      src={getUmbracoMediaUrl(image)}
                      width={325}
                      height={259}
                      alt={facilityName || "Facility Image"}
                    />
                  )}
                </StyledImageWrapper>
                <StyledItemContent>
                  {facilityName && <StyledItemName>{facilityName}</StyledItemName>}
                  <StyledDataList>
                    {address && (
                      <StyledDataItem>
                        <Location3Icon />
                        <address>{address}</address>
                      </StyledDataItem>
                    )}
                    {amountOfPeople && (
                      <StyledDataItem>
                        <UserIcon />
                        <span>{amountOfPeople}</span>
                      </StyledDataItem>
                    )}
                    {price && !isOffice(facilityType) && (
                      <StyledDataItem>
                        <PriceIcon />
                        <span>{isMeetingRoom(facilityType) ? `החל מ- ${price}` : price}</span>
                      </StyledDataItem>
                    )}
                  </StyledDataList>
                  {faciUrl && (
                    <StyledCardButton
                      variant="contained"
                      aria-label="facility detail button"
                      // @ts-ignore - MUI Button href typing
                      href={getNonUmbracoUrl(faciUrl)}
                      onClick={(e: any) => {
                        const href = getNonUmbracoUrl(faciUrl)
                        if (href.includes("#")) {
                          e.preventDefault()
                          const anchor = href.split("#")[1]
                          const element = document.getElementById(anchor)
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth" })
                          }
                        }
                      }}
                    >
                      {getCtaLabel(facilityType, t("learnMore"))}
                    </StyledCardButton>
                  )}
                </StyledItemContent>
              </StyledItem>
            ),
          )}
        </StyledItemList>
      </PortContainer>

      {totalPages > 1 && (
        <StyledPaginationContainer>
          <Pagination
            count={totalPages}
            page={currentPage}
            color="primary"
            shape="rounded"
            size="small"
            onChange={handlePageChange}
            siblingCount={1}
            boundaryCount={1}
          />
        </StyledPaginationContainer>
      )}
    </StyledSection>
  )
}

export default PortFacilitiesInnerPage
